import { useState, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { UiSearch, UiTable, UiButton } from "../../../components";
import { COUPON_COLUMNS, couponActionDropdownMenu, OFFERS_HISTORY_COLUMNS, offersActionDropdownMenu, COUPONS_HISTORY_COLUMNS, couponsHistoryActionDropdownMenu } from "./config";
import "./style.scss";
import { Dropdown, Menu, Space } from "antd";
import { actionDots } from "../../../assets/images";
import { CUSTOMERMANAGEMENT, COUPONMANAGEMENT, DISCOUNTMANAGEMENT } from "../../../apiServices/endpoints";
import * as api from "../../../actions";
import { searchInitiateDelayTime, tablePageSizeOptions } from "../../../utils/appUtils";
import { loaderReducer } from "../../../reducer/loader.reducer";
import OffersHistory from "./history/offerHistory";
import CouponsHistory from "./history/couponsHistory";
import OfferDrawer from "./couponDrawer/offerDrawer";
import GenerateCouponDrawer from "./couponDrawer/generateCouponDrawer";
import dayjs from "dayjs";
import { showSuccessNotification, showErrorNotification } from "../../../components/uiNotification";

const Coupon = () => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const debounceTimeOut = useRef(null);
    const [coupon, setCoupon] = useState({ filter: { searchFilter: "", searchTermValue: "", page: 0, size: 10 }, data: [], totalElements: 0 });
    const [offers, setOffers] = useState({ filter: { searchFilter: "", searchTermValue: "", page: 0, size: 10, month: null, startDate: null, endDate: null }, data: [], totalElements: 0 });
    const [couponsHistory, setCouponsHistory] = useState({ filter: { searchFilter: "", searchTermValue: "", page: 0, size: 10, customerId: "", month: null, startDate: null, endDate: null }, data: [], totalElements: 0, stats: { total: 0, used: 0, unused: 0 } });
    const [viewState, setViewState] = useState({
        showOffersHistory: false,
        showCouponsHistory: false
    });
    const [selection, setSelection] = useState({ customer: null, couponId: null });
    const [drawerState, setDrawerState] = useState({
        newOfferDrawerOpen: false,
        generateCouponDrawerOpen: false
    });
    const [newOfferFormData, setNewOfferFormData] = useState({ offerName: "", startDate: null, endDate: null, offerPercentage: "", imageFile: [], day: "", discountId: null });
    const [generateCouponFormData, setGenerateCouponFormData] = useState({ couponPercentage: "", startDate: null, endDate: null, dayCount: "", description: "", prefix: "" });
    const [modalState, setModalState] = useState({
        deleteModalOpen: false,
        couponToDelete: null,
        closeOfferModalOpen: false,
        offerToClose: null,
        imageModalOpen: false,
        selectedImages: [],
        previewImage: ""
    });


    const getCouponCustomerData = (page, size, searchTerm) => api.get(`${COUPONMANAGEMENT}/page/customer?page=${page}&size=${size}${searchTerm ? `&search=${searchTerm}` : ""}`);
    const getOffersHistoryData = (filter) => api.get(`${DISCOUNTMANAGEMENT}/page?page=${filter?.page}&size=${filter?.size}${filter?.searchTermValue ? `&search=${filter?.searchTermValue}` : ""}${filter?.month ? `&month=${filter?.month}` : ""}${filter?.startDate ? `&startDate=${filter?.startDate}` : ""}${filter?.endDate ? `&endDate=${filter?.endDate}` : ""}`);
    const getCouponsHistoryData = (filter) => api.get(`${COUPONMANAGEMENT}/cumulative_coupon/${filter?.customerId}?page=${filter?.page}&size=${filter?.size}${filter?.searchTermValue ? `&search=${filter?.searchTermValue}` : ""}${filter?.month ? `&month=${filter?.month}` : ""}${filter?.startDate ? `&startDate=${filter?.startDate}` : ""}${filter?.endDate ? `&endDate=${filter?.endDate}` : ""}`);
    const getCouponDetails = (couponId) => api.get(`${COUPONMANAGEMENT}/${couponId}`);
    const updateCoupon = (couponId, payload) => api.put(`${COUPONMANAGEMENT}/${couponId}`, payload);
    const deleteCoupon = (couponId) => api.del(`${COUPONMANAGEMENT}/cancel/${couponId}`);
    const createOffer = (payload) => api.post(`${DISCOUNTMANAGEMENT}`, payload);
    const generateCoupon = (payload) => api.post(`${COUPONMANAGEMENT}`, payload);
    const updateDiscount = (discountId, payload) => api.put(`${DISCOUNTMANAGEMENT}/${discountId}`, payload);
    const closeOffer = (discountId) => api.del(`${DISCOUNTMANAGEMENT}/cancel/${discountId}`);

    const { isFetching: isFetchingCouponData } = useQuery(
        ["FETCH_COUPON_DATA", coupon?.filter?.page, coupon?.filter?.size, coupon?.filter?.searchTermValue],
        () => getCouponCustomerData(coupon?.filter?.page, coupon?.filter?.size, coupon?.filter?.searchTermValue),
        {
            onSuccess: (response) => {
                if (response?.statusCode === 200 || response?.status === "OK") {
                    const content = response?.result?.couponForCustomers?.content || [];
                    setCoupon(prev => ({ ...prev, data: content, totalElements: response?.result?.couponForCustomers?.totalElements || 0 }));
                } else {
                    setCoupon(prev => ({ ...prev, data: [], totalElements: 0 }));
                }
            },
            enabled: Boolean(coupon?.filter?.page >= 0 || coupon?.filter?.size || coupon?.filter?.searchTermValue),
            refetchOnWindowFocus: false
        }
    );

    const { isFetching: isFetchingOffersData, refetch: refetchOffersData } = useQuery(
        ["FETCH_OFFERS_HISTORY", offers?.filter],
        () => getOffersHistoryData(offers?.filter),
        {
            onSuccess: (response) => {
                if (response?.statusCode === 200 || response?.status === "OK") {
                    const content = response?.result?.discountResponsePage?.content || [];
                    setOffers(prev => ({ ...prev, data: content, totalElements: response?.result?.discountResponsePage?.totalElements || 0 }));
                } else {
                    setOffers(prev => ({ ...prev, data: [], totalElements: 0 }));
                }
            },
            enabled: viewState?.showOffersHistory && Boolean(offers?.filter?.page >= 0 || offers?.filter?.size),
            refetchOnWindowFocus: false
        }
    );

    const { isFetching: isFetchingCouponsHistory, refetch: refetchCouponsHistory } = useQuery(
        ["FETCH_COUPONS_HISTORY", couponsHistory?.filter],
        () => getCouponsHistoryData(couponsHistory?.filter),
        {
            onSuccess: (response) => {
                if (response?.statusCode === 200 || response?.status === "OK") {
                    const result = response?.result?.customerCumulativeCouponResponse;
                    const content = result?.couponResponses?.content || [];
                    setCouponsHistory(prev => ({
                        ...prev,
                        data: content,
                        totalElements: result?.couponResponses?.totalElements || 0,
                        stats: {
                            total: result?.totalCoupon || 0,
                            used: result?.usedCoupon || 0,
                            unused: result?.unUsedCoupon || 0
                        }
                    }));
                    if (result) {
                        setSelection(prev => ({
                            ...prev,
                            customer: {
                                ...prev?.customer,
                                profileImage: result?.profileUrl ? `${CUSTOMERMANAGEMENT}/file${result?.profileUrl}` : null,
                                customerName: result?.customerName,
                                customerId: result?.customerId,
                                customerNo: result?.customerNo
                            }
                        }));
                    }
                } else {
                    setCouponsHistory(prev => ({ ...prev, data: [], totalElements: 0, stats: { total: 0, used: 0, unused: 0 } }));
                }
            },
            enabled: viewState?.showCouponsHistory && Boolean(couponsHistory?.filter?.customerId),
            refetchOnWindowFocus: false
        }
    );

    const { isFetching: isFetchingCouponDetails } = useQuery(
        ["FETCH_COUPON_DETAILS", selection?.couponId],
        () => getCouponDetails(selection?.couponId),
        {
            onSuccess: (response) => {
                if (response?.statusCode === 200 || response?.status === "OK") {
                    const result = response?.result?.couponResponse;
                    if (result) {
                        setGenerateCouponFormData({
                            couponPercentage: result?.couponPercentage,
                            startDate: result?.validFrom ? dayjs(result?.validFrom).format("YYYY-MM-DD") : null,
                            endDate: result?.validTo ? dayjs(result?.validTo).format("YYYY-MM-DD") : null,
                            dayCount: result?.dayCount,
                            description: result?.description ? result.description.slice(0, 150) : "",
                            prefix: result?.prefix || ""
                        });
                        setDrawerState(prev => ({ ...prev, generateCouponDrawerOpen: true }));
                    }
                }
            },
            enabled: Boolean(selection?.couponId),
            refetchOnWindowFocus: false
        }
    );

    const { isFetching: isFetchingCreateOffer } = useQuery(
        ["CREATE_OFFER"],
        () => createOffer(newOfferFormData),
        {
            onSuccess: (response) => {
                if (response?.statusCode === 200 || response?.data?.statusCode === 200) {
                    showSuccessNotification("Success", "Offer created successfully");
                    handleNewOfferDrawerClose();
                    refetchOffersData();
                } else {
                    showErrorNotification("Error", response?.message || "Failed to create offer");
                }
            },
            enabled: false,
            refetchOnWindowFocus: false
        }
    );

    const { isFetching: isFetchingUpdateOffer } = useQuery(
        ["UPDATE_OFFER"],
        () => updateDiscount(newOfferFormData?.discountId, {
            discount: {
                discountName: newOfferFormData?.offerName,
                day: Number(newOfferFormData?.day),
                startDate: newOfferFormData?.startDate || null,
                endDate: newOfferFormData?.endDate || null,
                discountPercentage: Number(newOfferFormData?.offerPercentage)
            }
        }),
        {
            onSuccess: (response) => {
                if (response?.statusCode === 200 || response?.data?.statusCode === 200) {
                    showSuccessNotification("Success", "Offer updated successfully");
                    handleNewOfferDrawerClose();
                    refetchOffersData();
                } else {
                    showErrorNotification("Error", response?.message || "Failed to update offer");
                }
            },
            enabled: false,
            refetchOnWindowFocus: false
        }
    );

    const { isFetching: isFetchingGenerateCoupon } = useQuery(
        ["GENERATE_COUPON"],
        () => generateCoupon({ ...generateCouponFormData, customerId: selection?.customer?.customerId }),
        {
            onSuccess: (response) => {
                if (response?.statusCode === 200 || response?.statusCode === 201 || response?.data?.statusCode === 200 || response?.data?.statusCode === 201) {
                    showSuccessNotification("Success", response?.result?.createReceipt || "Coupon generated successfully");
                    handleGenerateCouponDrawerClose();
                    refetchCouponsHistory();
                } else {
                    showErrorNotification("Error", response?.message || "Failed to generate coupon");
                }
            },
            enabled: false,
            refetchOnWindowFocus: false
        }
    );

    const { isFetching: isFetchingUpdateCoupon } = useQuery(
        ["UPDATE_COUPON"],
        () => updateCoupon(selection?.couponId, {
            customerId: selection?.customer?.customerId,
            prefix: generateCouponFormData?.prefix,
            description: generateCouponFormData?.description,
            couponPercentage: Number(generateCouponFormData?.couponPercentage),
            dayCount: Number(generateCouponFormData?.dayCount),
            validFrom: generateCouponFormData?.startDate || null,
            validTo: generateCouponFormData?.endDate || null,
            generatedBy: sessionStorage?.getItem("userId")
        }),
        {
            onSuccess: (response) => {
                if (response?.statusCode === 200 || response?.data?.statusCode === 200) {
                    showSuccessNotification("Success", "Coupon updated successfully");
                    handleGenerateCouponDrawerClose();
                    refetchCouponsHistory();
                } else {
                    showErrorNotification("Error", response?.message || "Failed to update coupon");
                }
            },
            enabled: false,
            refetchOnWindowFocus: false
        }
    );

    const { isFetching: isFetchingCloseOffer } = useQuery(
        ["CLOSE_OFFER"],
        () => closeOffer(modalState?.offerToClose),
        {
            onSuccess: (response) => {
                if (response?.statusCode === 200 || response?.data?.statusCode === 200) {
                    showSuccessNotification("Success", "Offer closed successfully");
                    handleCloseOfferCancel();
                    refetchOffersData();
                } else {
                    showErrorNotification("Error", response?.message || "Failed to close offer");
                }
            },
            enabled: false,
            refetchOnWindowFocus: false
        }
    );

    const { isFetching: isFetchingDeleteCoupon } = useQuery(
        ["DELETE_COUPON"],
        () => deleteCoupon(modalState?.couponToDelete),
        {
            onSuccess: (response) => {
                if (response?.statusCode === 200 || response?.data?.statusCode === 200) {
                    showSuccessNotification("Success", "Coupon deleted successfully");
                    handleDeleteCancel();
                    refetchCouponsHistory();
                } else {
                    showErrorNotification("Error", response?.message || "Failed to delete coupon");
                }
            },
            enabled: false,
            refetchOnWindowFocus: false
        }
    );

    const calculateDayCount = (startDate, endDate) => {
        if (startDate && endDate) {
            const diff = dayjs(endDate).diff(dayjs(startDate), 'day');
            return diff >= 0 ? diff + 1 : 0;
        }
        return "";
    };

    const handleDateChange = (date, type) => {
        const fromDate = dayjs(date?.[0]).format("YYYY-MM-DD");
        const toDate = dayjs(date?.[1]).format("YYYY-MM-DD");
        if (date) {
            if (fromDate && toDate) {
                if (type === "offers") {
                    setOffers(prev => ({ ...prev, filter: { ...prev.filter, startDate: fromDate, endDate: toDate, month: null } }));
                } else if (type === "couponsHistory") {
                    setCouponsHistory(prev => ({ ...prev, filter: { ...prev.filter, startDate: fromDate, endDate: toDate, month: null } }));
                }
            }
        } else {
            if (type === "offers") {
                setOffers(prev => ({ ...prev, filter: { ...prev.filter, startDate: null, endDate: null } }));
            } else if (type === "couponsHistory") {
                setCouponsHistory(prev => ({ ...prev, filter: { ...prev.filter, startDate: null, endDate: null } }));
            }
        }
    };

    const handleMonthChange = (month, type) => {
        if (type === "offers") {
            if (month) {
                setOffers(prev => ({ ...prev, filter: { ...prev.filter, month: month, startDate: null, endDate: null } }));
            } else {
                setOffers(prev => ({ ...prev, filter: { ...prev.filter, month: null } }));
            }
        } else if (type === "couponsHistory") {
            if (month) {
                setCouponsHistory(prev => ({ ...prev, filter: { ...prev.filter, month: month, startDate: null, endDate: null } }));
            } else {
                setCouponsHistory(prev => ({ ...prev, filter: { ...prev.filter, month: null } }));
            }
        }
    };

    const handleSearch = (event, type) => {
        const value = event?.target?.value;
        if (type === "coupon") {
            setCoupon(prev => ({ ...prev, filter: { ...prev.filter, searchFilter: value } }));
        } else if (type === "offers") {
            setOffers(prev => ({ ...prev, filter: { ...prev.filter, searchFilter: value } }));
        } else if (type === "couponsHistory") {
            setCouponsHistory(prev => ({ ...prev, filter: { ...prev.filter, searchFilter: value } }));
        }
        debounceSearch(value, type);
    };

    const debounceSearch = (searchTerm, type) => {
        if (debounceTimeOut?.current) {
            clearTimeout(debounceTimeOut?.current);
        }
        if (searchTerm !== "") {
            debounceTimeOut.current = setTimeout(() => {
                if (type === "coupon") {
                    setCoupon(prev => ({ ...prev, filter: { ...prev.filter, searchTermValue: searchTerm, page: 0 } }));
                } else if (type === "offers") {
                    setOffers(prev => ({ ...prev, filter: { ...prev.filter, searchTermValue: searchTerm, page: 0 } }));
                } else if (type === "couponsHistory") {
                    setCouponsHistory(prev => ({ ...prev, filter: { ...prev.filter, searchTermValue: searchTerm, page: 0 } }));
                }
            }, searchInitiateDelayTime);
        } else {
            if (type === "coupon") {
                setCoupon(prev => ({ ...prev, filter: { ...prev.filter, searchTermValue: "", page: 0 } }));
            } else if (type === "offers") {
                setOffers(prev => ({ ...prev, filter: { ...prev.filter, searchTermValue: "", page: 0 } }));
            } else if (type === "couponsHistory") {
                setCouponsHistory(prev => ({ ...prev, filter: { ...prev.filter, searchTermValue: "", page: 0 } }));
            }
        }
    };

    const handlePagination = (page, size, type) => {
        if (type === "coupon") {
            setCoupon(prev => ({ ...prev, filter: { ...prev.filter, page: page - 1, size: size } }));
        } else if (type === "offers") {
            setOffers(prev => ({ ...prev, filter: { ...prev.filter, page: page - 1, size: size } }));
        } else if (type === "couponsHistory") {
            setCouponsHistory(prev => ({ ...prev, filter: { ...prev.filter, page: page - 1, size: size } }));
        }
    };

    const handleCouponMenuClick = (menuType, detail) => {
        if (menuType === "viewProfile") {
            setSelection(prev => ({ ...prev, customer: detail }));
            setCouponsHistory(prev => ({ ...prev, filter: { ...prev.filter, customerId: detail?.customerId } }));
            setViewState(prev => ({ ...prev, showCouponsHistory: true }));
        } else if (menuType === "sendCoupons") {
            setSelection(prev => ({ ...prev, customer: detail }));
            setDrawerState(prev => ({ ...prev, generateCouponDrawerOpen: true }));
        }
    };

    const handleOffersMenuClick = (menuType, detail) => {
        if (menuType === "editOffer") {
            const imageFiles = detail?.discountImages?.map((img, index) => ({
                uid: index,
                name: `File-${index + 1}`,
                status: 'done',
                url: `${DISCOUNTMANAGEMENT}/file${img}`
            })) || [];
            setNewOfferFormData({
                offerName: detail?.discountName,
                startDate: detail?.startDate ? dayjs(detail?.startDate).format("YYYY-MM-DD") : null,
                endDate: detail?.endDate ? dayjs(detail?.endDate).format("YYYY-MM-DD") : null,
                offerPercentage: detail?.discountPercentage,
                day: detail?.day,
                imageFile: imageFiles,
                discountId: detail?.discountId
            });
            setDrawerState(prev => ({ ...prev, newOfferDrawerOpen: true }));
        } else if (menuType === "viewImage") {
            const images = detail?.discountImages?.map(img => `${DISCOUNTMANAGEMENT}/file${img}`) || [];
            setModalState(prev => ({ ...prev, selectedImages: images, imageModalOpen: true }));
        } else if (menuType === "closeOffer") {
            setModalState(prev => ({ ...prev, offerToClose: detail?.discountId, closeOfferModalOpen: true }));
        }
    };

    const handleCouponsHistoryMenuClick = (menuType, detail) => {
        if (menuType === "editCoupon") {
            setSelection(prev => ({ ...prev, couponId: detail?.couponId }));
        } else if (menuType === "deleteCoupon") {
            setModalState(prev => ({ ...prev, couponToDelete: detail?.couponId, deleteModalOpen: true }));
        }
    };

    const handleShowOffersHistory = () => {
        setViewState(prev => ({ ...prev, showOffersHistory: true }));
    };

    const handleBackFromOffersHistory = () => {
        setViewState(prev => ({ ...prev, showOffersHistory: false }));
        setOffers(prev => ({ ...prev, filter: { searchFilter: "", searchTermValue: "", page: 0, size: 10, month: null, startDate: null, endDate: null } }));
    };

    const handleBackFromCouponsHistory = () => {
        setViewState(prev => ({ ...prev, showCouponsHistory: false }));
        setSelection({ customer: null, couponId: null });
        setCouponsHistory({ filter: { searchFilter: "", searchTermValue: "", page: 0, size: 10, customerId: "", month: null, startDate: null, endDate: null }, data: [], totalElements: 0, stats: { total: 0, used: 0, unused: 0 } });
    };

    const handleNewOfferDrawerOpen = () => {
        setDrawerState(prev => ({ ...prev, newOfferDrawerOpen: true }));
    };

    const handleNewOfferDrawerClose = () => {
        setDrawerState(prev => ({ ...prev, newOfferDrawerOpen: false }));
        setNewOfferFormData({ offerName: "", startDate: null, endDate: null, offerPercentage: "", imageFile: [], day: "", discountId: null });
    };

    const handleNewOfferInputChange = (field, value) => {
        setNewOfferFormData(prev => {
            let updatedValue = value;
            if ((field === "startDate" || field === "endDate") && value) {
                updatedValue = dayjs(value).format("YYYY-MM-DD");
            }
            const updated = { ...prev, [field]: updatedValue };
            if (field === "startDate" || field === "endDate") {
                updated.day = calculateDayCount(updated.startDate, updated.endDate);
            }
            return updated;
        });
    };

    const urlToFile = async (url, filename, mimeType) => {
        const res = await fetch(url);
        const blob = await res.blob();
        return new File([blob], filename, { type: mimeType });
    };

    const handleNewOfferSubmit = async () => {
        const formData = new FormData();
        const discount = {
            discountName: newOfferFormData?.offerName,
            day: Number(newOfferFormData?.day),
            startDate: newOfferFormData?.startDate || null,
            endDate: newOfferFormData?.endDate || null,
            discountPercentage: Number(newOfferFormData?.offerPercentage)
        };
        formData?.append("discount", new Blob([JSON.stringify(discount)], { type: "application/json" }));

        const fileLists = newOfferFormData?.imageFile || [];
        for (const file of fileLists) {
            if (file?.originFileObj) {
                formData.append("Files", file.originFileObj);
            } else if (file?.url) {
                const filename = file.url.split("/").pop() || file.name;
                const mimeType = file.type || "application/octet-stream";
                const convertedFile = await urlToFile(file.url, filename, mimeType);
                formData.append("Files", convertedFile);
            }
        }

        if (newOfferFormData?.discountId) {
            queryClient.prefetchQuery(["UPDATE_OFFER"], () => updateDiscount(newOfferFormData?.discountId, formData));
        } else {
            queryClient.prefetchQuery(["CREATE_OFFER"], () => createOffer(formData));
        }
    };

    const handleGenerateCouponDrawerOpen = () => {
        setDrawerState(prev => ({ ...prev, generateCouponDrawerOpen: true }));
    };

    const handleGenerateCouponDrawerClose = () => {
        setDrawerState(prev => ({ ...prev, generateCouponDrawerOpen: false }));
        setGenerateCouponFormData({ couponPercentage: "", startDate: null, endDate: null, dayCount: "", description: "", prefix: "" });
        setSelection(prev => ({ ...prev, couponId: null }));
    };

    const handleGenerateCouponInputChange = (field, value) => {
        setGenerateCouponFormData(prev => {
            let updatedValue = value;
            if (field === "description" && value) {
                updatedValue = value.slice(0, 150);
            }
            if ((field === "startDate" || field === "endDate") && value) {
                updatedValue = dayjs(value).format("YYYY-MM-DD");
            }
            const updated = { ...prev, [field]: updatedValue };
            if (field === "startDate" || field === "endDate") {
                updated.dayCount = calculateDayCount(updated?.startDate, updated?.endDate);
            }
            return updated;
        });
    };

    const handleGenerateCouponSubmit = () => {
        if (selection?.couponId) {
            queryClient.prefetchQuery(["UPDATE_COUPON"], () => updateCoupon(selection?.couponId, {
                customerId: selection?.customer?.customerId,
                prefix: generateCouponFormData?.prefix,
                description: generateCouponFormData?.description,
                couponPercentage: Number(generateCouponFormData?.couponPercentage),
                dayCount: Number(generateCouponFormData?.dayCount),
                validFrom: generateCouponFormData?.startDate || null,
                validTo: generateCouponFormData?.endDate || null,
                generatedBy: sessionStorage?.getItem("userId")
            }));
        } else {
            const payload = {
                customerId: selection?.customer?.customerId,
                prefix: generateCouponFormData?.prefix,
                description: generateCouponFormData?.description,
                couponPercentage: Number(generateCouponFormData?.couponPercentage),
                dayCount: Number(generateCouponFormData?.dayCount),
                validFrom: generateCouponFormData?.startDate || null,
                validTo: generateCouponFormData?.endDate || null,
                generatedBy: sessionStorage?.getItem("userId")
            };
            queryClient.prefetchQuery(["GENERATE_COUPON"], () => generateCoupon(payload));
        }
    };

    const handleDeleteConfirm = () => {
        queryClient.prefetchQuery(["DELETE_COUPON"], () => deleteCoupon(modalState?.couponToDelete));
    };

    const handleDeleteCancel = () => {
        setModalState(prev => ({ ...prev, deleteModalOpen: false, couponToDelete: null }));
    };

    const handleCloseOfferConfirm = () => {
        queryClient.prefetchQuery(["CLOSE_OFFER"], () => closeOffer(modalState?.offerToClose));
    };

    const handleCloseOfferCancel = () => {
        setModalState(prev => ({ ...prev, closeOfferModalOpen: false, offerToClose: null }));
    };

    useEffect(() => {
        dispatch(loaderReducer(isFetchingCouponData || isFetchingOffersData || isFetchingCouponsHistory || isFetchingCreateOffer || isFetchingGenerateCoupon || isFetchingCouponDetails || isFetchingUpdateCoupon || isFetchingDeleteCoupon || isFetchingUpdateOffer || isFetchingCloseOffer));
    }, [dispatch, isFetchingCouponData, isFetchingOffersData, isFetchingCouponsHistory, isFetchingCreateOffer, isFetchingGenerateCoupon, isFetchingCouponDetails, isFetchingUpdateCoupon, isFetchingDeleteCoupon, isFetchingUpdateOffer, isFetchingCloseOffer]);

    const menuList = (menuItems, list, handleClick) => {
        return (
            <Menu>
                {menuItems?.map((menuDetails) => {
                    return (
                        <Menu.Item
                            key={menuDetails?.key}
                            onClick={(event) => {
                                event?.domEvent?.stopPropagation();
                                handleClick(menuDetails?.key, list);
                            }}
                        >
                            {menuDetails?.label}
                        </Menu.Item>
                    );
                })}
            </Menu>
        );
    };
    const couponActionRender = (list) => {
        const menuItems = couponActionDropdownMenu;
        return (
            <div style={{ textAlign: "center" }} onClick={(event) => event?.stopPropagation()}>
                <Dropdown overlay={menuList(menuItems, list, handleCouponMenuClick)} trigger={["click"]}>
                    <Space onClick={(event) => {
                        event?.preventDefault();
                        event?.stopPropagation();
                    }} style={{ justifyContent: "center", width: "100%", cursor: "pointer" }}>
                        <img src={actionDots} alt="Action" />
                    </Space>
                </Dropdown>
            </div>
        );
    };
    const offersActionRender = (list) => {
        const menuItems = offersActionDropdownMenu;
        return (
            <div style={{ textAlign: "center" }} onClick={(event) => event?.stopPropagation()}>
                <Dropdown overlay={menuList(menuItems, list, handleOffersMenuClick)} trigger={["click"]}>
                    <Space onClick={(event) => {
                        event?.preventDefault();
                        event?.stopPropagation();
                    }} style={{ justifyContent: "center", width: "100%", cursor: "pointer" }}>
                        <img src={actionDots} alt="Action" />
                    </Space>
                </Dropdown>
            </div>
        );
    };
    const couponsHistoryActionRender = (list) => {
        const menuItems = couponsHistoryActionDropdownMenu;
        return (
            <div style={{ textAlign: "center" }} onClick={(event) => event?.stopPropagation()}>
                <Dropdown overlay={menuList(menuItems, list, handleCouponsHistoryMenuClick)} trigger={["click"]}>
                    <Space onClick={(event) => {
                        event?.preventDefault();
                        event?.stopPropagation();
                    }} style={{ justifyContent: "center", width: "100%", cursor: "pointer" }}>
                        <img src={actionDots} alt="Action" />
                    </Space>
                </Dropdown>
            </div>
        );
    };

    const handleCouponRowClick = (customerDetail, event) => {
        if (event?.target?.closest("button") || event?.target?.closest(".ant-dropdown-trigger") || event?.target?.closest("img")) {
            return;
        }
        handleCouponMenuClick("viewProfile", customerDetail);
    };
    const couponColumns = COUPON_COLUMNS(couponActionRender);
    const offersColumns = OFFERS_HISTORY_COLUMNS(offersActionRender);
    const couponsHistoryColumns = COUPONS_HISTORY_COLUMNS(couponsHistory?.filter?.page, couponsHistory?.filter?.size, couponsHistoryActionRender);
    const totalCouponCount = coupon?.totalElements || 0;
    const shouldShowCouponPagination = totalCouponCount > 10;
    if (viewState?.showCouponsHistory) {
        return (
            <CouponsHistory
                customerData={selection?.customer}
                couponsData={couponsHistory?.data}
                totalElements={couponsHistory?.totalElements}
                stats={couponsHistory?.stats}
                filter={couponsHistory?.filter}
                setFilter={(filter) => setCouponsHistory(prev => ({ ...prev, filter }))}
                columns={couponsHistoryColumns}
                onBack={handleBackFromCouponsHistory}
                onSearch={(event) => handleSearch(event, "couponsHistory")}
                onDateChange={(date) => handleDateChange(date, "couponsHistory")}
                onMonthChange={(month) => handleMonthChange(month, "couponsHistory")}
                onPagination={(page, size) => handlePagination(page, size, "couponsHistory")}
                onAddCoupon={handleGenerateCouponDrawerOpen}
                drawerOpen={drawerState?.generateCouponDrawerOpen}
                formData={generateCouponFormData}
                onDrawerClose={handleGenerateCouponDrawerClose}
                onInputChange={handleGenerateCouponInputChange}
                onSubmit={handleGenerateCouponSubmit}
                deleteModalOpen={modalState?.deleteModalOpen}
                onDeleteConfirm={handleDeleteConfirm}
                onDeleteCancel={handleDeleteCancel}
                isEditMode={Boolean(selection?.couponId)}
            />
        );
    }
    if (viewState?.showOffersHistory) {
        return (
            <OffersHistory
                offersData={offers?.data}
                totalElements={offers?.totalElements}
                filter={offers?.filter}
                setFilter={(filter) => setOffers(prev => ({ ...prev, filter }))}
                columns={offersColumns}
                drawerOpen={drawerState?.newOfferDrawerOpen}
                formData={newOfferFormData}
                onBack={handleBackFromOffersHistory}
                onSearch={(event) => handleSearch(event, "offers")}
                onDateChange={(date) => handleDateChange(date, "offers")}
                onMonthChange={(month) => handleMonthChange(month, "offers")}
                onPagination={(page, size) => handlePagination(page, size, "offers")}
                onOffersSend={handleNewOfferDrawerOpen}
                onDrawerClose={handleNewOfferDrawerClose}
                onInputChange={handleNewOfferInputChange}
                onSubmit={handleNewOfferSubmit}
                closeOfferModalOpen={modalState?.closeOfferModalOpen}
                onCloseOfferConfirm={handleCloseOfferConfirm}
                onCloseOfferCancel={handleCloseOfferCancel}
                imageModalOpen={modalState?.imageModalOpen}
                selectedImages={modalState?.selectedImages}
                onImageModalClose={() => setModalState(prev => ({ ...prev, imageModalOpen: false, selectedImages: [] }))}
                previewImage={modalState?.previewImage}
                setPreviewImage={(val) => setModalState(prev => ({ ...prev, previewImage: val }))}
            />
        );
    }
    return (
        <div>
            <div className="flexible" style={{ alignItems: "center" }}>
                <div>
                    <h6 style={{ padding: "10px", margin: 0, whiteSpace: "nowrap" }}>Coupons/Offers</h6>
                </div>
                <div className="button-group">
                    <UiSearch
                        placeholder="Customer Name /ID"
                        onChange={(event) => handleSearch(event, "coupon")}
                        value={coupon?.filter?.searchFilter}
                        style={{ width: 250 }}
                    />
                    <UiButton
                        className="offers-history-btn"
                        onClick={handleShowOffersHistory}
                    >
                        Offers History
                    </UiButton>
                    <UiButton
                        className="offers-send-btn"
                        onClick={handleNewOfferDrawerOpen}
                    >
                        + Offers Send
                    </UiButton>
                </div>
            </div>
            <UiTable
                columns={couponColumns}
                dataSource={coupon?.data}
                onRow={(customerDetail) => ({
                    onClick: (event) => handleCouponRowClick(customerDetail, event)
                })}
                scroll={{ x: "max-content", y: "calc(100vh - 220px)" }}
                pagination={!shouldShowCouponPagination ? false : {
                    current: coupon?.filter?.page + 1,
                    pageSize: coupon?.filter?.size || 10,
                    total: totalCouponCount,
                    pageSizeOptions: tablePageSizeOptions,
                    showSizeChanger: true,
                    onChange: (page, size) => handlePagination(page, size, "coupon")
                }}
            />
            <OfferDrawer
                open={drawerState?.newOfferDrawerOpen}
                formData={newOfferFormData}
                onClose={handleNewOfferDrawerClose}
                onInputChange={handleNewOfferInputChange}
                onSubmit={handleNewOfferSubmit}
            />
            <GenerateCouponDrawer
                open={drawerState?.generateCouponDrawerOpen}
                customerData={selection?.customer}
                formData={generateCouponFormData}
                onClose={handleGenerateCouponDrawerClose}
                onInputChange={handleGenerateCouponInputChange}
                onSubmit={handleGenerateCouponSubmit}
                isEditMode={Boolean(selection?.couponId)}
            />
        </div>
    );
};

export default Coupon;

